import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useTenantId } from './useTenant';

/**
 * GET /api/tenants/<id>/vehicles/<vehicleId>/gps-report?days=N — where this
 * vehicle actually spent its time, computed from bulk-imported telematics
 * history (core/gps_report.py). Same ?days= convention as useAnalytics(),
 * so the period-picker UI pattern (see GPS_REPORT_PERIODS below) is
 * identical between the two.
 *
 * Returns { insufficient_data, summary, sites, daily } — insufficient_data
 * is true (summary null) when fewer than 2 pings exist in the period, which
 * is a normal answer (nothing imported yet, or the period misses the
 * import), not an error.
 */
export function useGpsReport(vehicleId, days = 30) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['gps-report', tenantId, vehicleId, days],
    queryFn: () => api(`/api/tenants/${tenantId}/vehicles/${vehicleId}/gps-report?days=${days}`),
    enabled: Boolean(tenantId) && Boolean(vehicleId),
    staleTime: 60_000,
  });
}

/** Day / week / month / 6 mėn. / 12 mėn. — see import.field.* naming
 *  convention notes; labels live at gpsReport.period<N> in the locale
 *  files. */
export const GPS_REPORT_PERIODS = [1, 7, 30, 180, 365];

/**
 * GET /api/tenants/<id>/gps-fleet-report?days=N — the fleet-wide rollup of
 * every vehicle's own build_report(): which quarries (own and competitor)
 * get visited most, by how many distinct trucks, across the whole fleet.
 * See core/gps_report.py's aggregate_fleet() docstring. Same ?days=
 * convention as useGpsReport()/useAnalytics() above.
 *
 * Returns { vehicles_with_data, vehicles_total, fleet_own_quarry_minutes,
 * fleet_own_quarry_visits, fleet_competitor_quarry_minutes,
 * fleet_competitor_quarry_visits, quarries, estimated_leakage_eur,
 * period_start, period_end }.
 */
export function useGpsFleetReport(days = 30) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['gps-fleet-report', tenantId, days],
    queryFn: () => api(`/api/tenants/${tenantId}/gps-fleet-report?days=${days}`),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
}
